import React, { useState, useEffect } from 'react'
import { AlertTriangle, Loader2, X } from 'lucide-react'

interface SecureDeleteModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => Promise<void>
    title: string
    description: React.ReactNode
    verificationText: string
    confirmButtonText?: string
    isDeleting?: boolean
    error?: string | null
}

export const SecureDeleteModal: React.FC<SecureDeleteModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    verificationText,
    confirmButtonText = "Delete Permanently",
    isDeleting = false,
    error
}) => {
    const [inputValue, setInputValue] = useState('')
    const [localError, setLocalError] = useState<string | null>(null)

    // Reset input on open
    useEffect(() => {
        if (isOpen) {
            setInputValue('')
            setLocalError(null)
        }
    }, [isOpen])

    // Update local error if prop error changes
    useEffect(() => {
        if (error) setLocalError(error)
    }, [error])

    if (!isOpen) return null

    const isMatch = inputValue === verificationText
    const canConfirm = isMatch && !isDeleting

    const handleConfirm = async () => {
        if (!canConfirm) return
        setLocalError(null)
        try {
            await onConfirm()
            onClose()
        } catch (err) {
            // Error handling is usually done by parent passing 'error' prop, 
            // but we catch here just in case promise rejects without updating prop
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div
                className="w-full max-w-md bg-gray-900 border border-red-500/20 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/5 bg-red-500/5">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/10 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">{title}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-white transition-colors"
                        disabled={isDeleting}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    <div className="text-sm text-gray-300 leading-relaxed">
                        {description}
                    </div>

                    <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                            To confirm, type <span className="text-red-400/90 font-mono select-all font-bold">"{verificationText}"</span> below:
                        </label>
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={verificationText}
                            className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg focus:border-red-500/50 focus:ring-1 focus:ring-red-500/50 outline-none text-white font-mono text-sm placeholder-gray-700 transition-all"
                            autoFocus
                            disabled={isDeleting}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && canConfirm) handleConfirm()
                                if (e.key === 'Escape') onClose()
                            }}
                            onPaste={(e) => {
                                e.preventDefault() // Optional: force typing? No, pasting filename is fine for UX.
                                const text = e.clipboardData.getData('text')
                                setInputValue(text)
                            }}
                        />
                    </div>

                    {localError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
                            {localError}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-white/5 border-t border-white/5 flex gap-3 justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                        disabled={isDeleting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                        className={`
                            flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                            ${canConfirm
                                ? 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-900/20'
                                : 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-50'}
                        `}
                    >
                        {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {confirmButtonText}
                    </button>
                </div>
            </div>
        </div>
    )
}
